import { lazy, type ComponentType, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Center, Loader } from '@mantine/core';
import { Marco } from './componentes/Marco.tsx';
import { usePerfil } from './sesion.ts';
import { Login } from './paginas/Login.tsx';
import { Recuperar, Restablecer } from './paginas/Recuperar.tsx';
import { CambiarPassword } from './paginas/CambiarPassword.tsx';
import { Configurar2fa } from './paginas/Configurar2fa.tsx';
import { RUTAS } from './rutas.ts';

// Cada pantalla de adentro se baja recién cuando se abre (el login y el marco, de entrada).
// Si una pestaña abierta antes de un despliegue pide una pantalla del build anterior, ya
// no existe: se recarga una vez para tomar la versión nueva (no en bucle).
function pantalla<T extends Record<string, unknown>>(cargar: () => Promise<T>, nombre: keyof T) {
  return lazy(async () => {
    try {
      const m = await cargar();
      sessionStorage.removeItem('epms:recargado');
      return { default: m[nombre] as ComponentType };
    } catch (e) {
      if (!sessionStorage.getItem('epms:recargado')) {
        sessionStorage.setItem('epms:recargado', '1');
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw e;
    }
  });
}
const MiCuenta = pantalla(() => import('./paginas/MiCuenta.tsx'), 'MiCuenta');
const ClientesLista = pantalla(() => import('./paginas/ClientesLista.tsx'), 'ClientesLista');
const ClienteFicha = pantalla(() => import('./paginas/ClienteFicha.tsx'), 'ClienteFicha');
const UsuariosLista = pantalla(() => import('./paginas/UsuariosLista.tsx'), 'UsuariosLista');
const UsuarioFicha = pantalla(() => import('./paginas/UsuarioFicha.tsx'), 'UsuarioFicha');
const SincronizacionArca = pantalla(() => import('./paginas/SincronizacionArca.tsx'), 'SincronizacionArca');
const Dashboard = pantalla(() => import('./paginas/Dashboard.tsx'), 'Dashboard');
const ClientesConsulta = pantalla(() => import('./paginas/ClientesConsulta.tsx'), 'ClientesConsulta');
const ClienteConsulta = pantalla(() => import('./paginas/ClienteConsulta.tsx'), 'ClienteConsulta');
const Bandeja = pantalla(() => import('./paginas/Bandeja.tsx'), 'Bandeja');
const Vencimientos = pantalla(() => import('./paginas/Vencimientos.tsx'), 'Vencimientos');
const ConfigDocumentos = pantalla(() => import('./paginas/ConfigDocumentos.tsx'), 'ConfigDocumentos');
const Conciliar = pantalla(() => import('./paginas/Conciliar.tsx'), 'Conciliar');
const Cumplimiento = pantalla(() => import('./paginas/Cumplimiento.tsx'), 'Cumplimiento');
const Ayuda = pantalla(() => import('./paginas/Ayuda.tsx'), 'Ayuda');

const Cargando = () => <Center h="100vh"><Loader /></Center>;

/** Exige sesión y que el usuario haya completado lo obligatorio (contraseña, 2FA). */
function ConSesion({ children }: { children: ReactNode }) {
  const { data: perfil, isLoading } = usePerfil();
  const { pathname } = useLocation();
  if (isLoading) return <Cargando />;
  if (!perfil) return <Navigate to="/login" replace />;
  if (perfil.debeCambiarPassword && pathname !== '/cambiar-password') return <Navigate to="/cambiar-password" replace />;
  if (!perfil.debeCambiarPassword && perfil.debeConfigurar2fa && pathname !== '/configurar-2fa') return <Navigate to="/configurar-2fa" replace />;
  return children;
}

function SoloAdmin({ children }: { children: ReactNode }) {
  const { data: perfil } = usePerfil();
  return perfil?.rol === 'admin' ? children : <Navigate to={RUTAS.inicio} replace />;
}

export function App() {
  const { data: perfil, isLoading } = usePerfil();
  return (
    <Routes>
      <Route path="/login" element={isLoading ? <Cargando /> : perfil ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/recuperar" element={<Recuperar />} />
      <Route path="/restablecer" element={<Restablecer />} />
      <Route path="/cambiar-password" element={<ConSesion><CambiarPassword /></ConSesion>} />
      <Route path="/configurar-2fa" element={<ConSesion><Configurar2fa /></ConSesion>} />
      <Route element={<ConSesion><Marco /></ConSesion>}>
        <Route index element={<Navigate to={RUTAS.inicio} replace />} />
        {/* Uso diario */}
        <Route path="inicio" element={<Dashboard />} />
        <Route path="clientes" element={<ClientesConsulta />} />
        <Route path="clientes/:id" element={<ClienteConsulta />} />
        <Route path="conciliar" element={<Conciliar />} />
        <Route path="cumplimiento" element={<Cumplimiento />} />
        <Route path="bandeja" element={<Bandeja />} />
        <Route path="vencimientos" element={<Vencimientos />} />
        {/* Configuración */}
        <Route path="config/usuarios" element={<SoloAdmin><UsuariosLista /></SoloAdmin>} />
        <Route path="config/usuarios/:id" element={<SoloAdmin><UsuarioFicha /></SoloAdmin>} />
        <Route path="config/clientes" element={<ClientesLista />} />
        <Route path="config/clientes/nuevo" element={<ClienteFicha />} />
        <Route path="config/clientes/:id" element={<ClienteFicha />} />
        <Route path="config/documentos" element={<SoloAdmin><ConfigDocumentos /></SoloAdmin>} />
        <Route path="sincronizacion" element={<SincronizacionArca />} />
        <Route path="mi-cuenta" element={<MiCuenta />} />
        <Route path="ayuda" element={<Ayuda />} />
        <Route path="*" element={<Navigate to={RUTAS.inicio} replace />} />
      </Route>
    </Routes>
  );
}
